import { useCallback, useState } from 'react';
import { logStatsigEvent } from 'src/app/atoms/AbTesting/statsigClient';
import { submitHubSpotFormBuilder, SubmissionValuesData } from 'src/functions/hubspot/hubspot.service';
import useExternalNavigation from 'src/hooks/useExternalNavigation';
import { useFreeTrialFormContext } from 'src/hooks/useFreeTrialFormContext';
import useHubspotFormCreation from 'src/hooks/useHubspotFormCreation';

const FREE_TRIAL_FORM_ID = '33e2b1c3-ed0a-48da-a447-035d8782912e';
const FREE_TRIAL_URL = 'https://cohost.mn.co/?plan=free-trial';

interface FreeTrialHubspotFormHandler {
  formId: string;
  isFormLoading: boolean;
  isLoading: boolean;
  isSubmitted: boolean;
  submitFormAndNavigate: () => void;
}

interface UseFreeTrialHubspotFormHandlerOptions {
  formWrapperId: string;
  statsigEventKey?: string;
  extraQueryParams?: Record<string, string>;
}

const useFreeTrialHubspotFormHandler = ({
  formWrapperId,
  statsigEventKey = 'free_trial_email_block_submit',
  extraQueryParams,
}: UseFreeTrialHubspotFormHandlerOptions): FreeTrialHubspotFormHandler => {
  const { isSubmitted, markAsSubmitted, setPendingEmail, consumePendingEmail } = useFreeTrialFormContext();
  const { navigateToExternal } = useExternalNavigation(extraQueryParams);
  const [isFormLoading, setIsFormLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const submitForm = useCallback(() => {
    const submitHubSpotForm = submitHubSpotFormBuilder(`#${formWrapperId}`);
    submitHubSpotForm();
  }, [formWrapperId]);

  const submitFormAndNavigate = useCallback(() => {
    const emailInput = document.querySelector<HTMLInputElement>(`#${formWrapperId} input[name="email"]`);
    if (emailInput?.value) {
      setPendingEmail(emailInput.value);
    }
    setIsLoading(true);
    submitForm();
  }, [submitForm, formWrapperId, setPendingEmail]);

  const handleFormSubmitted = useCallback(
    (_form: HTMLFormElement, data: { redirectUrl: string; submissionValues: SubmissionValuesData }) => {
      setIsLoading(false);
      markAsSubmitted();
      logStatsigEvent(statsigEventKey, 1);

      const email = consumePendingEmail() || data.submissionValues.email || '';
      const encodedEmail = encodeURIComponent(email);
      const targetUrl = email ? `${FREE_TRIAL_URL}&email=${encodedEmail}` : FREE_TRIAL_URL;

      navigateToExternal(targetUrl);
    },
    [markAsSubmitted, consumePendingEmail, statsigEventKey, navigateToExternal]
  );

  useHubspotFormCreation({
    formId: FREE_TRIAL_FORM_ID,
    formWrapperId,
    onFormReadyCallback: () => setIsFormLoading(false),
    onFormSubmitCallback: () => {
      // Capture email for Enter key submissions via the active element
      const activeInput = document.activeElement as HTMLInputElement;
      if (activeInput?.name === 'email' && activeInput.value) {
        setPendingEmail(activeInput.value);
      }
    },
    onFormSubmittedCallback: handleFormSubmitted,
    errorCallback: () => setIsFormLoading(false),
  });

  return {
    formId: FREE_TRIAL_FORM_ID,
    isFormLoading,
    isLoading,
    isSubmitted,
    submitFormAndNavigate,
  };
};

export default useFreeTrialHubspotFormHandler;
